// store/slices/walletSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import walletService, {
  DepositData,
  WithdrawalData,
  TransactionFilters,
} from "../../services/walletService";

interface WalletState {
  balance: {
    xrp_balance: number;
    estimated_usd: number;
  } | null;
  depositAddress: {
    address: string;
    label: string;
    note: string;
  } | null;
  transactions: any[];
  botEarnings: any[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
  isLoading: boolean;
  error: string | null;
  depositLoading: boolean;
  withdrawalLoading: boolean;
}

const initialState: WalletState = {
  balance: null,
  depositAddress: null,
  transactions: [],
  botEarnings: [],
  totalPages: 0,
  currentPage: 1,
  totalItems: 0,
  isLoading: false,
  error: null,
  depositLoading: false,
  withdrawalLoading: false,
};

export const getBalance = createAsyncThunk(
  "wallet/getBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getBalance();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch balance",
      );
    }
  },
);

export const getDepositAddress = createAsyncThunk(
  "wallet/getDepositAddress",
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getDepositAddress();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch deposit address",
      );
    }
  },
);

export const deposit = createAsyncThunk(
  "wallet/deposit",
  async (depositData: DepositData, { rejectWithValue }) => {
    try {
      const response = await walletService.deposit(depositData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Deposit failed");
    }
  },
);

export const withdraw = createAsyncThunk(
  "wallet/withdraw",
  async (withdrawalData: WithdrawalData, { rejectWithValue }) => {
    try {
      const response = await walletService.withdraw(withdrawalData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Withdrawal failed",
      );
    }
  },
);

export const getTransactions = createAsyncThunk<
  any,
  TransactionFilters | undefined,
  { rejectValue: string }
>("wallet/getTransactions", async (filters, { rejectWithValue }) => {
  try {
    const response = await walletService.getTransactions(filters);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch transactions",
    );
  }
});

export const getBotEarnings = createAsyncThunk<
  any,
  TransactionFilters | undefined,
  { rejectValue: string }
>("wallet/getBotEarnings", async (filters, { rejectWithValue }) => {
  try {
    const response = await walletService.getBotEarnings(filters);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch bot earnings",
    );
  }
});

export const getTradingStatus = createAsyncThunk(
  "wallet/getTradingStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getTradingStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trading status",
      );
    }
  },
);

export const getAirdrops = createAsyncThunk(
  "wallet/getAirdrops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getAirdrops();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch airdrops",
      );
    }
  },
);

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTransactionStatus: (state) => {
      state.depositLoading = false;
      state.withdrawalLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Balance
      .addCase(getBalance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.balance = action.payload;
      })
      .addCase(getBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Deposit Address
      .addCase(getDepositAddress.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepositAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.depositAddress = action.payload;
      })
      .addCase(getDepositAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Deposit
      .addCase(deposit.pending, (state) => {
        state.depositLoading = true;
        state.error = null;
      })
      .addCase(deposit.fulfilled, (state) => {
        state.depositLoading = false;
      })
      .addCase(deposit.rejected, (state, action) => {
        state.depositLoading = false;
        state.error = action.payload as string;
      })

      // Withdraw
      .addCase(withdraw.pending, (state) => {
        state.withdrawalLoading = true;
        state.error = null;
      })
      .addCase(withdraw.fulfilled, (state) => {
        state.withdrawalLoading = false;
      })
      .addCase(withdraw.rejected, (state, action) => {
        state.withdrawalLoading = false;
        state.error = action.payload as string;
      })

      // Get Transactions
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload.data || action.payload || [];
        state.totalPages = action.payload.last_page || 1;
        state.currentPage = action.payload.current_page || 1;
        state.totalItems =
          action.payload.total ||
          (action.payload.data ? action.payload.data.length : 0);
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Bot Earnings
      .addCase(getBotEarnings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBotEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.botEarnings = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
      })
      .addCase(getBotEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Trading Status
      .addCase(getTradingStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTradingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Airdrops
      .addCase(getAirdrops.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAirdrops.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearTransactionStatus } = walletSlice.actions;
export default walletSlice.reducer;
